import { User } from '@features/users/types';
import { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllUsersAreInStateStatus, selectGetAllUsersStatus, selectUsers } from '@features/users/store/selectors';
import { useTranslation } from 'react-i18next';
import { getAllUsersRequest, getUserRequest } from '@features/users/store';
import { Autocomplete, CircularProgress, TextFieldProps } from '@mui/material';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import TextField from '@mui/material/TextField';
import { ROLES } from '@app/permissions/roles';

interface UserSelectorProps {
    value: User['pk'] | null;
    onChange: (event: SyntheticEvent, newUserOption: User | null) => void;
    TextFieldProps?: TextFieldProps;
    inputHeight?: number;
    label?: string;
    usersRole?: ROLES;
    [x: string]: any;
}

const UserSelector: FC<UserSelectorProps> = ({
    value,
    onChange,
    TextFieldProps,
    inputHeight,
    label,
    usersRole,
    ...restProps
}) => {
    const [userAutocompleteOpen, setUserAutocompleteOpen] = useState(false);
    const allUsersAreInState = useSelector(selectAllUsersAreInStateStatus);
    const getAllUsersStatus = useSelector(selectGetAllUsersStatus);
    const users: User[] = useSelector(selectUsers);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const userObject = useMemo(() => {
        return users.find((u: User) => String(u.pk) === String(value));
    }, [value, users]);

    useEffect(() => {
        if (value && !users.find((u) => u.pk === value)) {
            // @ts-ignore
            dispatch(getUserRequest(value));
        }
    }, [value, users, dispatch]);

    const handleOpenUserAutocomplete = () => {
        setUserAutocompleteOpen(true);

        if (!allUsersAreInState) {
            // @ts-ignore
            dispatch(getAllUsersRequest(usersRole));
        }
    };

    const handleCloseUserAutoComplete = () => {
        setUserAutocompleteOpen(false);
    };

    return (
        <Autocomplete
            size="small"
            open={userAutocompleteOpen}
            onOpen={handleOpenUserAutocomplete}
            onClose={handleCloseUserAutoComplete}
            value={userObject || null}
            onChange={onChange}
            isOptionEqualToValue={(option: User, value: User) => option.pk === value.pk}
            getOptionLabel={(user: User) => {
                if (!user) {
                    return '';
                }

                return `${user.first_name} ${user.last_name}`;
            }}
            options={users}
            loading={getAllUsersStatus === REQUEST_STATUS.LOADING}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label || t('created_by')}
                    placeholder={`${t('name')}...`}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            sx: {
                                minHeight: inputHeight || 42,
                            },
                            endAdornment: (
                                <>
                                    {getAllUsersStatus === REQUEST_STATUS.LOADING ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                    {...TextFieldProps}
                />
            )}
            {...restProps}
        />
    );
};

export default UserSelector;
